"use client";

/**
 * /launch — the Breakin' Bread pro-launch pitch for the keynote at Google
 * Stockholm, 13 Nov 2026 (Gordon: "update the launch pitch page for the event
 * … put it inside the app in the body, show header, menu, footer and vu meter
 * still"; "work on the design and the animations"; "add the bb releases in the
 * circles spinning at 33rpm").
 *
 * Two modes:
 *  - Scroll (default): the slides as full-height sections inside the app,
 *    building in as they scroll into view.
 *  - Present: the whole app goes full screen — header, menu, media player and
 *    VU meter stay (Gordon: "launch present mode should show the header,
 *    menu, media player footer, and vu meter but on full screen") — and the
 *    keyboard or a presentation clicker steps the slides (→ ↓ Space PageDown /
 *    ← ↑ PageUp). Slides always fill the space between header and player.
 *
 * Same visual system as the home intro: the app theme's colours (--launch-*
 * in globals.css map to the active theme), Oswald impact type, release
 * artwork, the BB mark signing the last slide.
 * Everything is LEFT-aligned like Home (Gordon 2026-10-29: "left align all
 * graphics … same for launch page"); at most one spinning record per slide.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { ArrowRight, Eye, Globe2, Handshake, Heart, KeyRound, Maximize2, Minimize2, Pause, Play, Share2, ShieldCheck, UploadCloud, Zap } from "lucide-react";
import { shareContent } from "@/lib/share";
import { useToast } from "@/hooks/use-toast";
import { getVisibleReleases } from "@/services/home";
import { VinylDisc } from "@/components/launch/vinyl-disc";
import { GraphicSlideshow } from "@/components/launch/graphic-slideshow";
import { BuyVsStream } from "@/components/launch/buy-vs-stream";
import { BBSignature } from "@/components/home/bb-signature";
import { FOUNDING_CIRCLE_GROUPS, FOUNDING_CIRCLE_JOIN, FOUNDING_CIRCLE_PERKS } from "@/data/founding-circle";
import { keepBrandTogether } from "@/lib/brand";
import { cn } from "@/lib/utils";

const LAUNCH_URL = "https://www.breakinbread.com/launch";

// Every tool on the platform today, grouped the way makers use them (Gordon:
// "add all the other tools in the studio section and bb academy").
const TOOL_GROUPS: { labelKey: string; tools: string[] }[] = [
  { labelKey: "devStudioLabel", tools: ["toolArtwork", "toolFilm", "toolDj", "toolRecorder", "toolSongwriter", "toolLive", "toolJam", "toolPodcast", "toolPublisher", "toolEpx"] },
  { labelKey: "devReleaseLabel", tools: ["toolMastering", "toolScore", "toolSync", "toolMerch", "toolEvents", "toolParty"] },
  // LyriaDJ is kept off /launch — it gets its own reveal at Slush, after the BB event.
  { labelKey: "devLearnLabel", tools: ["toolAcademy", "toolAdvisor"] },
  // The business side (Gordon 2026-11-02: "page 07 is missing administration").
  { labelKey: "devAdminLabel", tools: ["toolRights", "toolSplitShares", "toolVault", "toolBookkeeping", "toolContracts", "toolStats"] },
];

// Register once: everything a maker registers on BB, and the path from one
// upload to the societies and the streaming services (Gordon: "registering a
// track, release, video, artwork, event onboarding and guidance need to be a
// launch section … registering to copyright societies, like stim, sami, and
// release directly on streaming platforms … awal"). Steps 2–3 are not built
// yet — today BB only IMPORTS STIM and AWAL catalogues — so they carry the
// "in development" badge. Drop it per step as each ships.
const REGISTER_KINDS = ["kindTrack", "kindRelease", "kindVideo", "kindArtwork", "kindEvent", "kindSync"];
const REGISTER_STEPS = [
  { Icon: UploadCloud, key: "registerUpload", soon: false },
  { Icon: ShieldCheck, key: "registerRights", soon: true },
  { Icon: Globe2, key: "registerRelease", soon: true },
];

// Standing on the shoulders of… (names as Gordon wrote them; notes are keys
// when they need translating).
const TRIBUTE: { name: string; note?: string; noteKey?: string; more?: boolean }[] = [
  { name: "Christian Falk", noteKey: "tributeFalk" },
  { name: "DJ Rock Ski", note: "Ayodele Shekoni" },
  { name: "Gordon Cyrus", note: "aka Gee" },
  { name: "Desmond Foster" },
  { name: "Andrés Avellan" },
  { name: 'Daniel "Papa Dee" Wahlgren' },
  { name: "Peter Lax", noteKey: "tributeMemory" },
  { name: "Jhoan Camitz", noteKey: "tributeCamitz" },
  { name: "Marimba Roney" },
  { name: "Nine Yards", note: "Wayne Beckford · Aeon Manahan · Clevedon Buntyn" },
  { name: "Dr Alex Jonsson" },
  { name: "DLX Music", note: "Stefan Englund · Viktor Englund · Nicklas Jarl · Stefan Fandén · Rolf Petterson · Simon · Ulf Söderberg", more: true },
  { name: 'Daniel "Puppet" Blomkvist' },
  { name: "Oskar Hagelbäck", noteKey: "tributeHagelback" },
  { name: 'Adam "ADL" Baptiste' },
  { name: "Freddie Cruger", noteKey: "tributeCruger" },
  { name: "Damon Frost", note: "aka D-Frost aka Data Bwoy" },
  { name: "Aaron Phiri" },
  { name: 'Richard "Swingfly" Silva' },
  { name: 'Gary "Leafnuts" Beckford' },
  { name: 'Ayodele "Ayo The Jitz" Ayaji' },
  { name: "DJ Sleepy" },
  { name: "Daddy Boastin'" },
  { name: "The Latin Kings", note: 'Douglas "Dogge Doggelito" Leon · Christian "Salla" Salazar · Hugo "Chepe" Salazar' },
  { name: "Två Blåbär" },
  { name: "Happy Alright", note: "Paul Malmström · Mikael Sundin · Ante Runnquist · Björn Sunde · Christian Olsson · Goran Kajfes" },
  { name: "Soundtrade Studios", note: "Lars Gustavsson · CG Langenskiöld · Åke Karlson · Monica Möllberg" },
  { name: "Sonox", note: 'Anders Engström (former EMI Publishing A&R) · Mattias "125.cc" Vangsnes · Patrik "Pawal" Wahlström · and the team · CDs pressed by DCM, Kista (CG Langenskiöld)' },
];

function shuffled<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * A slide: children with `bb-reveal` build in. From tablet up every slide is
 * EXACTLY the space between the header and the footer (Gordon 2026-10-31:
 * "when scrolling down with arrow keys make sure each section fits perfectly
 * between header and footer") — a slide whose content is taller is scaled
 * down to fit, so stepping always frames one whole slide. Phones scroll as
 * before. --launch-footer = the player bar (+ the VU strip on phones).
 */
function Slide({ id, tone = "dark", children, className, background }: { id: string; tone?: "dark" | "light" | "accent"; children: ReactNode; className?: string; background?: ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const inner = innerRef.current, slide = inner?.closest<HTMLElement>("[data-slide]");
    if (!inner || !slide) return;
    const mq = window.matchMedia("(min-width: 640px)");
    let frame = 0;
    const fit = () => {
      inner.style.transform = ""; inner.style.width = ""; inner.style.marginBottom = "";
      if (!mq.matches) return;
      const cs = getComputedStyle(slide);
      const avail = slide.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      if (avail <= 0 || inner.scrollHeight <= avail) return;
      let scale = avail / inner.scrollHeight;
      // Scale down, keep the full width (a wider box reflows a little shorter,
      // so measure once more), and give back the height the scale frees.
      for (let pass = 0; pass < 2; pass++) {
        inner.style.width = `${100 / scale}%`;
        scale = Math.min(1, avail / inner.scrollHeight);
      }
      inner.style.width = `${100 / scale}%`;
      inner.style.transform = `scale(${scale})`;
      inner.style.marginBottom = `${-inner.scrollHeight * (1 - scale)}px`;
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
    schedule();
    const ro = new ResizeObserver(schedule);
    ro.observe(slide);
    mq.addEventListener("change", schedule);
    // Late images and fonts change heights.
    const late = setTimeout(schedule, 1500);
    window.addEventListener("load", schedule);
    return () => { cancelAnimationFrame(frame); ro.disconnect(); mq.removeEventListener("change", schedule); clearTimeout(late); window.removeEventListener("load", schedule); };
  }, []);
  return (
    <section
      id={id}
      data-slide
      className={cn(
        // Full viewport height: the slide's colour runs behind the see-through
        // header and footer, its content is padded clear of both (Gordon
        // 2026-10-31: "each section full viewport height now that the header
        // and footer are semi transparent").
        "bb-launch-slide relative flex min-h-[100dvh] sm:h-[100dvh] snap-start flex-col justify-center overflow-hidden px-6 sm:px-12 lg:px-20",
        // From sm up the top padding also clears the corner row under the
        // header (Present · Share · Auto-play · Join), so no slide starts under it.
        "pt-[calc(var(--header-height)_+_3rem)] pb-[calc(var(--launch-footer)_+_3rem)] sm:pt-[calc(var(--header-height)_*_2_+_0.75rem)] sm:pb-[calc(var(--launch-footer)_+_1.5rem)]",
        tone === "dark" && "bg-[var(--launch-bg)] text-[var(--launch-fg)]",
        tone === "light" && "bg-[var(--launch-fg)] text-[var(--launch-bg)]",
        tone === "accent" && "bg-[var(--launch-accent)] text-[var(--launch-on-accent)]",
        className,
      )}
    >
      {/* A full-size background (the release wall) sits outside the moving
          content, so it fills the whole slide on phones too (Gordon 2026-11-01). */}
      {background}
      {/* Two layers: the outer one carries the scroll parallax, the inner one
          the fit-to-screen scale, so the two transforms never fight. */}
      <div className="bb-parallax w-full will-change-transform">
        <div ref={innerRef} className="w-full origin-top-left">{children}</div>
      </div>
    </section>
  );
}

const Eyebrow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <p className={cn("bb-reveal text-sm sm:text-base lg:text-xl font-semibold uppercase tracking-[0.25em]", className)}>{children}</p>
);

export default function LaunchPage() {
  const t = useTranslations("launch");
  const ti = useTranslations("home.intro");
  const deckRef = useRef<HTMLDivElement>(null);
  const tw = useTranslations("whyBb");
  const tj = useTranslations("join");
  const tf = useTranslations("foundingCircle");
  const [covers, setCovers] = useState<string[]>([]);
  const [founders, setFounders] = useState<string[]>([]);
  const [graphics, setGraphics] = useState<string[]>([]);
  const [presenting, setPresenting] = useState(false);
  // iPhone Safari has no page full screen; no Present button there.
  const [canPresent, setCanPresent] = useState(false);

  // People only (Gordon 2026-11-01: "only user and artist thumbnails, not
  // releases"): founders first, then artists' and members' photos — never a
  // release cover. Server-side so visitors get members' public photos too.
  useEffect(() => {
    let alive = true;
    fetch("/api/launch/people")
      .then(r => r.json() as Promise<{ founders?: string[]; people?: string[] }>)
      .then(({ founders = [], people = [] }) => { if (alive) { setFounders(founders); setGraphics(shuffled(people)); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    getVisibleReleases().then(list => {
      if (alive) setCovers(shuffled([...new Set(list.map(r => r.imageUrl).filter(Boolean))]));
    });
    return () => { alive = false; };
  }, []);

  // Build each slide in once it is mostly on screen. Fail-safe for a live
  // keynote: content is visible by default and only hidden for the build-in
  // once the observer is confirmed working (data-anim); if it never reports
  // (old browser, odd webview), everything is revealed after 2.5 s.
  useEffect(() => {
    const root = deckRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    let reported = false;
    const io = new IntersectionObserver(
      entries => {
        if (!reported) { reported = true; root.setAttribute("data-anim", ""); }
        // "Mostly on screen" = 35 % of the slide, OR 40 % of the screen for a
        // slide taller than the phone — a tall slide never shows 35 % of
        // itself at once and stayed blank (Gordon 2026-11-01, slide 07).
        entries.forEach(e => {
          if (e.isIntersecting && (e.intersectionRatio >= 0.35 || e.intersectionRect.height >= window.innerHeight * 0.4)) e.target.setAttribute("data-visible", "");
        });
      },
      { threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.35] },
    );
    root.querySelectorAll("[data-slide]").forEach(sl => io.observe(sl));
    const fallback = setTimeout(() => {
      if (!reported) root.querySelectorAll("[data-slide]").forEach(sl => sl.setAttribute("data-visible", ""));
    }, 2500);
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, []);

  // Present mode = the whole app full screen, chrome included. Older Safari
  // only has the webkit-prefixed API; a refused request must not throw.
  useEffect(() => {
    const d = document as any, el = document.documentElement as any;
    setCanPresent(!!(d.fullscreenEnabled || d.webkitFullscreenEnabled) && !!(el.requestFullscreen || el.webkitRequestFullscreen));
    const onChange = () => setPresenting(!!(d.fullscreenElement || d.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => { document.removeEventListener("fullscreenchange", onChange); document.removeEventListener("webkitfullscreenchange", onChange); };
  }, []);
  // Parallax (Gordon 2026-10-31: "parallax scroll transitions on /launch"):
  // each slide's content glides, fades and eases in at its own pace while the
  // slide moves — at rest (no transform) when a slide sits in place, so
  // stepping and the fit stay exact. Off with reduced motion.
  useEffect(() => {
    const root = deckRef.current;
    if (!root || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const layers = [...root.querySelectorAll<HTMLElement>("[data-slide] > .bb-parallax")];
    let frame = 0;
    const update = () => {
      frame = 0;
      const vh = window.innerHeight || 1;
      for (const layer of layers) {
        const slide = layer.parentElement!;
        const p = Math.max(-1, Math.min(1, slide.getBoundingClientRect().top / vh)); // 0 = in place
        if (Math.abs(p) < 0.001) { layer.style.transform = ""; continue; }
        // Depth only (Gordon 2026-11-01: "fading too much … looks washy") — the
        // content stays solid; no opacity, no shrinking.
        layer.style.transform = `translate3d(0, ${(p * 12).toFixed(2)}vh, 0)`;
      }
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);

  // Sit exactly under the header's search button, wherever the layout puts it
  // (left or right handed, sidebar open or not).
  const [presentAt, setPresentAt] = useState<{ left: number; top: number; size: number } | null>(null);
  const { toast } = useToast();
  useEffect(() => {
    const place = () => {
      const r = document.querySelector('header a[aria-label="Search"]')?.getBoundingClientRect();
      if (r && r.width) setPresentAt({ left: r.left, top: r.bottom, size: r.width });
    };
    place();
    const mo = new MutationObserver(place);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-menu-side'] });
    window.addEventListener('resize', place);
    const late = setInterval(place, 1000); // the sidebar animates open / closed
    return () => { mo.disconnect(); window.removeEventListener('resize', place); clearInterval(late); };
  }, []);
  const togglePresent = () => {
    const d = document as any, el = document.documentElement as any;
    try {
      if (d.fullscreenElement || d.webkitFullscreenElement) void (d.exitFullscreen || d.webkitExitFullscreen).call(d)?.catch?.(() => {});
      else void (el.requestFullscreen || el.webkitRequestFullscreen).call(el)?.catch?.(() => {});
    } catch { /* refused — stays as it is */ }
  };

  // Keyboard / clicker stepping, in both modes: from the slide nearest the top
  // of the view to its neighbour. Slides are full viewport height and land at
  // the very top, their colour behind the see-through header and footer.
  const step = useCallback((dir: 1 | -1) => {
    const root = deckRef.current;
    if (!root) return;
    const slides = [...root.querySelectorAll<HTMLElement>("[data-slide]")];
    // Slides are full-viewport (behind the header), so each lands at the very top.
    const distance = (el: HTMLElement) => Math.abs(el.getBoundingClientRect().top);
    const here = slides.reduce((best, s, i) => (distance(s) < distance(slides[best]) ? i : best), 0);
    slides[Math.min(slides.length - 1, Math.max(0, here + dir))]?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.dispatchEvent(new Event("bb-launch-step")); // auto-play restarts its clock
  }, []);

  // Auto-play (Gordon 2026-11-01: "play automatically in an animation we just
  // play in a loop on a screen as well as click section by section. Export to
  // YouTube as a film"). Play button, or /launch?autoplay=1 for a screen.
  // Each slide stays as long as its text needs, then glides on; after the last
  // it fades back to the first and loops. Keys/clickers still step — the clock
  // restarts from wherever you land. ?film=1 = one pass with no controls, for
  // recording (scripts/launch-film); window.__bbLaunchFilm reports progress.
  const [autoplay, setAutoplay] = useState(false);
  const [film, setFilm] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.has("film")) {
      setFilm(true);
      // globals.css hides coπ (and anything else marked) in a recorded film.
      document.documentElement.setAttribute("data-launch-film", "");
    }
    // ?film=wait: hold until the recorder has the page loaded and says go.
    if (q.get("film") === "wait") {
      const go = () => setAutoplay(true);
      window.addEventListener("bb-launch-film-start", go, { once: true });
      return () => window.removeEventListener("bb-launch-film-start", go);
    }
    if (q.has("autoplay") || q.has("film")) setAutoplay(true);
  }, []);
  useEffect(() => {
    if (!autoplay) return;
    const root = deckRef.current;
    if (!root) return;
    const w = window as typeof window & { __bbLaunchFilm?: { slide: number; of: number; done: boolean } };
    let timer = 0, frame = 0, cancelled = false;
    const slides = () => [...root.querySelectorAll<HTMLElement>("[data-slide]")];
    const nearest = (all: HTMLElement[]) => all.reduce((best, s, i) => (Math.abs(s.getBoundingClientRect().top) < Math.abs(all[best].getBoundingClientRect().top) ? i : best), 0);
    // Time on a slide: 6 s + reading time for its words, 9–18 s.
    const holdMs = (el: HTMLElement) => Math.min(18000, Math.max(9000, 6000 + (el.innerText || "").trim().length * 28));
    // Our own eased scroll (not the browser's smooth scroll) so a recorder that
    // steps the clock frame by frame gets the same motion.
    const glide = (el: HTMLElement, ms = 1200) => new Promise<void>(done => {
      const from = window.scrollY, to = from + el.getBoundingClientRect().top, t0 = performance.now();
      const tick = (now: number) => {
        if (cancelled) return done();
        const p = Math.min(1, (now - t0) / ms), e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
        window.scrollTo(0, from + (to - from) * e);
        if (p < 1) frame = requestAnimationFrame(tick); else { el.setAttribute("data-visible", ""); done(); }
      };
      frame = requestAnimationFrame(tick);
    });
    const backToStart = (first: HTMLElement) => new Promise<void>(done => {
      root.style.transition = "opacity 600ms ease";
      root.style.opacity = "0";
      timer = window.setTimeout(() => {
        // Let every slide build in again on the next pass.
        slides().forEach(s => { if (s !== first) s.removeAttribute("data-visible"); });
        window.scrollTo(0, 0);
        root.style.opacity = "1";
        timer = window.setTimeout(() => { root.style.transition = ""; first.setAttribute("data-visible", ""); done(); }, 650);
      }, 650);
    });
    const schedule = (delay?: number) => {
      window.clearTimeout(timer);
      const all = slides();
      const here = nearest(all);
      if (film) w.__bbLaunchFilm = { slide: here + 1, of: all.length, done: false };
      timer = window.setTimeout(async () => {
        if (cancelled) return;
        const list = slides(), i = nearest(list);
        if (i + 1 < list.length) await glide(list[i + 1]);
        else if (film) { w.__bbLaunchFilm = { slide: list.length, of: list.length, done: true }; return; }
        else await backToStart(list[0]);
        if (!cancelled) schedule();
      }, delay ?? holdMs(all[here]));
    };
    const onStep = () => schedule(900 + holdMs(slides()[nearest(slides())]));
    window.addEventListener("bb-launch-step", onStep);
    schedule();
    // Keep the screen on while it plays.
    let lock: { release?: () => Promise<void> } | null = null;
    const wake = async () => { try { lock = await (navigator as any).wakeLock?.request("screen"); } catch { /* not allowed */ } };
    const onVis = () => { if (document.visibilityState === "visible") void wake(); };
    void wake();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      root.style.opacity = ""; root.style.transition = "";
      window.removeEventListener("bb-launch-step", onStep);
      document.removeEventListener("visibilitychange", onVis);
      void lock?.release?.().catch(() => {});
    };
  }, [autoplay, film]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) { e.preventDefault(); step(1); }
      else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  const half = Math.ceil(covers.length / 2);
  const wallRows = [covers.slice(0, half), covers.slice(half)].filter(r => r.length > 0);
  // ecc=H: up to 30 % of the code can be covered and it still scans — room for the BB logo in the middle.
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=480x480&margin=0&ecc=H&data=${encodeURIComponent(LAUNCH_URL)}`;

  return (
    <div
      ref={deckRef}
      // Pulled up behind the fixed header, so slide 1 is full height too.
      className="bb-launch relative -mt-[var(--header-height)] bg-[var(--launch-bg)] [--launch-footer:76px] sm:[--launch-footer:var(--player-height)]"
    >
      {/* Present toggle: just the icon, directly under the search button like
          the hero's close button (Gordon 2026-10-31) — follows search to the
          left in the mirrored layout, and sits above the right-edge VU column,
          which used to swallow the click on desktop. */}
      {canPresent && !film && (
        <button
          type="button"
          onClick={togglePresent}
          className="bb-edge-btn bb-edge-btn--bare fixed right-0 top-[var(--header-height)] z-[110] inline-flex size-[var(--header-height)] items-center justify-center p-0 menuright:left-0 menuright:right-auto"
          style={presentAt ? { left: presentAt.left, top: presentAt.top, right: 'auto', width: presentAt.size, height: presentAt.size } : undefined}
          aria-pressed={presenting}
          aria-label={presenting ? t("exitPresent") : t("present")}
          title={presenting ? t("exitPresent") : t("present")}
        >
          {presenting ? <Minimize2 className="h-5 w-5" aria-hidden /> : <Maximize2 className="h-5 w-5" aria-hidden />}
        </button>
      )}

      {/* Share (Gordon 2026-11-01: "share button on the launch page in a corner"):
          just the icon, right beside Present — the phone's share sheet, else the
          link copied. Takes Present's place where there's no full screen (iPhone). */}
      {!film && <button
        type="button"
        onClick={async () => {
          const ok = await shareContent({ title: t("shareTitle"), text: t("shareText"), url: "https://www.breakinbread.com/launch" });
          if (ok) toast({ description: t("shareCopied") });
        }}
        className="bb-edge-btn bb-edge-btn--bare fixed right-[var(--header-height)] top-[var(--header-height)] z-[110] inline-flex size-[var(--header-height)] items-center justify-center p-0 menuright:left-[var(--header-height)] menuright:right-auto"
        style={presentAt ? { left: presentAt.left + (canPresent ? presentAt.size : 0), top: presentAt.top, right: 'auto', width: presentAt.size, height: presentAt.size } : undefined}
        aria-label={t("share")}
        title={t("share")}
      >
        <Share2 className="h-5 w-5" aria-hidden />
      </button>}

      {/* Auto-play: beside Share — plays the slides by themselves, in a loop. */}
      {!film && <button
        type="button"
        onClick={() => setAutoplay(a => !a)}
        className="bb-edge-btn bb-edge-btn--bare fixed right-[calc(var(--header-height)*2)] top-[var(--header-height)] z-[110] inline-flex size-[var(--header-height)] items-center justify-center p-0 menuright:left-[calc(var(--header-height)*2)] menuright:right-auto"
        style={presentAt ? { left: presentAt.left + (canPresent ? 2 : 1) * presentAt.size, top: presentAt.top, right: 'auto', width: presentAt.size, height: presentAt.size } : undefined}
        aria-pressed={autoplay}
        aria-label={autoplay ? t("autoplayStop") : t("autoplay")}
        title={autoplay ? t("autoplayStop") : t("autoplay")}
      >
        {autoplay ? <Pause className="h-5 w-5" aria-hidden /> : <Play className="h-5 w-5" aria-hidden />}
      </button>}

      {/* Join (Gordon 2026-11-02): beside Auto-play, with its word — the board,
          the team or the Founding Circle, all on /join. Glass backing so the
          word stays readable over light and dark slides. */}
      {!film && <Link
        href="/dashboard?tab=join"
        className="bb-btn fixed right-[calc(var(--header-height)*3)] top-[var(--header-height)] z-[110] inline-flex h-[var(--header-height)] items-center gap-2 rounded-[var(--btn-radius)] border-2 border-foreground bg-background/70 px-3 text-sm font-semibold text-foreground backdrop-blur-md hover:bg-background menuright:left-[calc(var(--header-height)*3)] menuright:right-auto"
        style={presentAt ? { left: presentAt.left + (canPresent ? 3 : 2) * presentAt.size, top: presentAt.top, right: 'auto', height: presentAt.size } : undefined}
      >
        <Handshake className="h-4 w-4" aria-hidden />{tj("button")}
      </Link>}

      {/* 1 · Cover */}
      <Slide id="cover">
        <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_1fr]">
          <div className="space-y-6">
            <div className="bb-reveal flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-[var(--launch-accent)] px-4 py-1.5 text-sm font-bold uppercase tracking-[0.2em] text-[var(--launch-on-accent)]">{t("liveBeta")}</span>
              <span className="whitespace-nowrap text-[clamp(0.68rem,1.25vw,1rem)] font-semibold uppercase tracking-[0.15em] text-[var(--launch-accent)]">{t("eyebrow")}</span>
            </div>
            <h1 className="bb-impact bb-impact-tight text-[clamp(4rem,11vw,11rem)]" style={{ ["--bb-tight" as string]: "0.86" }}>
              <span className="bb-reveal block" style={{ ["--d" as string]: "120ms" }}>Breakin&rsquo;</span>
              <span className="bb-reveal block" style={{ ["--d" as string]: "240ms" }}>Bread</span>
            </h1>
            {/* Two rows, set close (Gordon 2026-11-02): "By music makers, / for music makers." */}
            <p className="bb-reveal bb-impact text-[clamp(1.75rem,3.6vw,3.5rem)] text-[var(--launch-accent)]" style={{ ["--d" as string]: "420ms", lineHeight: 1.08 }}>
              <span className="block">{ti("byMakers")},</span>
              <span className="block">{ti("forMakers")}</span>
            </p>
            <p className="bb-reveal text-base sm:text-xl font-medium opacity-80" style={{ ["--d" as string]: "560ms" }}>{t("venue")}</p>
          </div>
          {/* One record per slide (Gordon: "only 1 spinning circle on each launch section") */}
          <div className="mr-auto w-full max-w-[34rem]">
            <VinylDisc covers={covers} offset={0} className="w-full" />
          </div>
        </div>
      </Slide>

      {/* 2 · Where it started */}
      <Slide id="origin" tone="light">
        <div className="grid items-center gap-10 lg:grid-cols-[1.35fr_1fr]">
          <div className="space-y-8">
            <Eyebrow className="text-[var(--launch-accent-2)]">{t("originEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(2.75rem,6vw,6.75rem)]" style={{ ["--d" as string]: "150ms" }}>{t("originTitle")}</h2>
            <p className="bb-reveal max-w-2xl text-lg sm:text-2xl lg:text-3xl opacity-75" style={{ ["--d" as string]: "350ms" }}>{t("originBody")}</p>
            {/* Sonox.com, late '90s — the platform idea, too early (Gordon 2026-11-02). No names on this slide. */}
            <div className="bb-reveal max-w-2xl space-y-2 border-l-4 border-current pl-5" style={{ ["--d" as string]: "550ms" }}>
              <p className="text-sm sm:text-base font-bold uppercase tracking-[0.2em]">{t("originSonoxLabel")}</p>
              <p className="text-base sm:text-xl leading-relaxed opacity-75">{t("originSonox")}</p>
            </div>
            <p className="bb-reveal bb-impact max-w-2xl text-[clamp(1.5rem,2.6vw,2.5rem)]" style={{ ["--d" as string]: "700ms" }}>{t("originNext")}</p>
          </div>
          {/* The founders, on the record — in its own column, never over the headline */}
          <div className="mr-auto w-full max-w-[18rem] sm:max-w-[22rem] lg:max-w-[26rem]">
            {/* 2 s on each face, 2 s crossfade (Gordon 2026-11-01). */}
            <VinylDisc covers={[...founders, ...graphics.filter(g => !founders.includes(g))]} offset={0} holdMs={2000} fadeMs={2000} className="w-full" />
          </div>
        </div>
      </Slide>

      {/* 3 · The problem */}
      <Slide id="problem">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div className="space-y-8">
            <Eyebrow className="text-[var(--launch-accent)]">{t("problemEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(2.5rem,5.2vw,5.75rem)]" style={{ ["--d" as string]: "120ms" }}>{t("problemTitle")}</h2>
            {/* "Release, sell and license your music…" removed here (Gordon 2026-11-02). */}
          </div>
          <ol>
            {/* Your music. Your business. Transparency. (Gordon 2026-11-02) */}
            {[ti("heard"), ti("paid"), t("problemTransparency")].map((line, i) => (
              <li key={line} className="bb-reveal flex items-baseline gap-5 border-t border-foreground/15 py-4 last:border-b" style={{ ["--d" as string]: `${300 + i * 220}ms` }}>
                <span className="w-10 shrink-0 text-base font-semibold tracking-[0.2em] opacity-60">0{i + 1}</span>
                <span className="bb-impact text-[clamp(2.25rem,4.4vw,4.75rem)] text-[var(--launch-accent)]">{line}</span>
              </li>
            ))}
          </ol>
        </div>
      </Slide>

      {/* 4 · Manifesto, with a slideshow of makers' public graphics beside it */}
      <Slide id="manifesto" tone="light">
        {/* The slideshow starts level with 01, not with the title (Gordon 2026-11-02). */}
        <div className={cn("grid gap-x-10 gap-y-8", graphics.length > 0 && "lg:grid-cols-[1.4fr_1fr]")}>
          <div className="max-w-6xl space-y-8 lg:col-span-2">
            <Eyebrow className="text-[var(--launch-accent-2)]">{t("manifestoEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(3rem,7vw,7.5rem)]" style={{ ["--d" as string]: "120ms" }}>{ti("creatorsFirst")}</h2>
          </div>
          <div className="max-w-6xl">
            <ol className="space-y-6">
              {[[ti("ownWork"), t("ownWorkBody")], [ti("seeSplits"), t("seeSplitsBody")], [t("manifestoGetPaid"), t("noGatekeepersBody")]].map(([title, body], i) => (
                <li key={title} className="bb-reveal grid grid-cols-[4rem_1fr] sm:grid-cols-[6rem_1fr] items-baseline gap-4" style={{ ["--d" as string]: `${300 + i * 250}ms` }}>
                  <span className="bb-impact text-[clamp(2.5rem,5vw,4.5rem)] text-[var(--launch-accent-2)]">0{i + 1}</span>
                  <span>
                    <span className="bb-impact block text-[clamp(2rem,4.5vw,4.5rem)]">{title}</span>
                    <span className="mt-2 block text-lg sm:text-2xl opacity-70">{body}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          {graphics.length > 0 && (
            <GraphicSlideshow images={graphics} className="mr-auto w-full max-w-[26rem] lg:mt-3 lg:max-w-[min(56vh,32rem)] lg:self-start" />
          )}
        </div>
      </Slide>

      {/* 5 · The platform */}
      <Slide id="platform">
        <div className="space-y-12">
          <div className="space-y-6">
            <Eyebrow className="text-[var(--launch-accent)]">{t("platformEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(2.75rem,6.5vw,7rem)]" style={{ ["--d" as string]: "120ms" }}>{ti("wholePath")}</h2>
          </div>
          <div className="relative">
            {/* The path the three stops sit on */}
            <div className="bb-reveal absolute left-6 right-0 top-6 hidden h-0.5 bg-[linear-gradient(90deg,var(--launch-accent),transparent)] md:block" style={{ ["--d" as string]: "250ms" }} aria-hidden />
            <ol className="grid gap-10 md:grid-cols-3 md:gap-8">
              {/* 03 is the community (Gordon 2026-11-02: "change 3 to Community — find other creators and collaborations"). */}
              {[[ti("make"), t("makeBody")], [ti("release"), t("releaseBody")], [t("communityStop"), t("communityStopBody")]].map(([title, body], i) => (
                <li key={title} className="bb-reveal relative" style={{ ["--d" as string]: `${350 + i * 250}ms` }}>
                  <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[var(--launch-accent)] text-lg font-bold text-[var(--launch-on-accent)] ring-8 ring-[var(--launch-bg)]">0{i + 1}</span>
                  <p className="bb-impact mt-6 text-[clamp(2.5rem,4.5vw,4.75rem)]">{title}</p>
                  <p className="mt-3 max-w-sm text-lg sm:text-xl opacity-75">{body}</p>
                </li>
              ))}
            </ol>
          </div>
          <p className="bb-reveal text-sm sm:text-base font-semibold uppercase tracking-[0.35em] text-[var(--launch-accent)]" style={{ ["--d" as string]: "1100ms" }}>{t("pillars")}</p>
        </div>
      </Slide>

      {/* 6 · Register once — upload to BB, register with the societies, release everywhere */}
      <Slide id="register" tone="light">
        <div className="max-w-7xl space-y-8">
          <Eyebrow className="text-[var(--launch-accent-2)]">{t("registerEyebrow")}</Eyebrow>
          {/* One sentence per row (Gordon 2026-11-02: "Upload once. / Registered everywhere."). */}
          <h2 className="bb-reveal bb-impact text-[clamp(2.5rem,5.6vw,6rem)]" style={{ ["--d" as string]: "120ms" }}>
            {t("registerTitle").split(/(?<=[.!?。！？])\s*/).filter(Boolean).map((line, i) => <span key={i} className="block">{line}</span>)}
          </h2>
          <ul className="bb-reveal flex flex-wrap gap-2.5" style={{ ["--d" as string]: "260ms" }}>
            {REGISTER_KINDS.map(k => (
              <li key={k} className="rounded-full bg-[var(--launch-bg)] px-5 py-2 text-base sm:text-lg lg:text-xl font-semibold text-[var(--launch-fg)]">{t(k)}</li>
            ))}
          </ul>
          <ol className="grid gap-5 md:grid-cols-3">
            {REGISTER_STEPS.map(({ Icon, key, soon }, i) => (
              <li key={key} className="bb-reveal flex flex-col gap-3 rounded-3xl bg-[var(--launch-bg)] p-7 text-[var(--launch-fg)]" style={{ ["--d" as string]: `${420 + i * 250}ms` }}>
                <div className="flex items-center justify-between gap-3">
                  <Icon className="h-8 w-8 text-[var(--launch-accent)]" aria-hidden />
                  {soon && (
                    <span className="rounded-full bg-[var(--launch-accent)] px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-[var(--launch-on-accent)]">{t("registerSoon")}</span>
                  )}
                </div>
                <p className="bb-impact text-[clamp(1.75rem,2.6vw,2.75rem)]">
                  <span className="text-[var(--launch-accent)]">0{i + 1}</span> {t(`${key}Title`)}
                </p>
                <p className="text-base sm:text-lg leading-relaxed opacity-80">{t(`${key}Body`)}</p>
                {/* Why BB does it differently (Gordon 2026-11-02: "with a link to Why BB"). */}
                {key === "registerRelease" && (
                  <Link href="/why-bb" className="mt-auto inline-flex w-fit items-center gap-2 rounded-full border-2 border-current px-4 py-2 text-sm font-semibold hover:opacity-80">
                    {t("registerReleaseLink")} <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </Slide>

      {/* 7 · Always in development — the real tools, grouped */}
      <Slide id="tools">
        <div className="space-y-10">
          <div className="space-y-6">
            <Eyebrow>{t("devEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(2.75rem,6vw,6.5rem)]" style={{ ["--d" as string]: "120ms" }}>{t("devTitle")}</h2>
          </div>
          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-[1.5fr_1fr_1fr_1fr]">
            {TOOL_GROUPS.map((group, g) => (
              <div key={group.labelKey} className="bb-reveal" style={{ ["--d" as string]: `${300 + g * 250}ms` }}>
                <p className="border-b-2 border-current pb-2 text-sm sm:text-base font-bold uppercase tracking-[0.25em]">{t(group.labelKey)}</p>
                <ul className={cn("mt-4 space-y-1.5", group.tools.length > 6 && "sm:columns-2 sm:gap-x-8")}>
                  {group.tools.map(k => (
                    <li key={k} className="bb-impact break-inside-avoid text-[clamp(1.25rem,1.8vw,1.75rem)] leading-tight">
                      {t(k)}
                      {/* The Maker AI: built on BB's collective knowledge (Gordon 2026-11-02). */}
                      {k === "toolAdvisor" && <span className="block font-sans text-sm sm:text-base font-normal normal-case tracking-normal opacity-75">{t("toolAdvisorSub")}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* 7b · The community — trade skills with each other (Gordon 2026-11-02:
          "push the community more: swapping music services with each other, the
          motley crew of professionals in and around the music business"). */}
      <Slide id="community" tone="light">
        <div className="space-y-8">
          <div className="space-y-6">
            <Eyebrow className="text-[var(--launch-accent)]">{t("communityEyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact text-[clamp(2.75rem,6.5vw,7rem)]" style={{ ["--d" as string]: "120ms" }}>{t("communityTitle")}</h2>
            <p className="bb-reveal max-w-3xl text-lg sm:text-2xl leading-relaxed opacity-85" style={{ ["--d" as string]: "300ms" }}>{t("communityBody")}</p>
          </div>
          <ul className="flex max-w-6xl flex-wrap gap-2 sm:gap-3">
            {t("communityRoles").split(/\s*[,，、،]\s*/).filter(Boolean).map((role, i) => (
              <li key={role} className="bb-reveal bb-impact rounded-full border-2 border-current px-4 py-1.5 text-[clamp(1.1rem,1.8vw,1.75rem)] leading-tight" style={{ ["--d" as string]: `${450 + i * 60}ms` }}>
                {role}
              </li>
            ))}
          </ul>
          <Link href="/people" className="bb-reveal inline-flex items-center gap-2 rounded-full border-2 border-current px-5 py-2.5 text-base font-semibold hover:opacity-80" style={{ ["--d" as string]: "900ms" }}>
            {t("communityCta")} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </Slide>

      {/* 8 · The music is already here — live release wall */}
      <Slide id="proof" className="px-0 sm:px-0 lg:px-0" background={
        <>
        {wallRows.length > 0 && (
          <div className="absolute inset-0 flex flex-col justify-center gap-3 opacity-60" aria-hidden>
            {wallRows.map((row, r) => (
              <div key={r} className={cn("bb-intro-drift flex h-[46%] w-max gap-3", r === 1 && "bb-intro-drift-reverse")}>
                {[...row, ...row].map((src, i) => (
                  <div key={i} className="relative h-full aspect-square shrink-0 overflow-hidden rounded-2xl">
                    <Image src={src} alt="" fill sizes="30vh" loading="eager" className="object-cover" />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        <div className="absolute inset-0 bg-[linear-gradient(90deg,hsl(var(--background)/0.9)_0%,hsl(var(--background)/0.62)_55%,hsl(var(--background)/0.3)_100%)]" aria-hidden />
        </>
      }>
        <div className="relative z-10 space-y-6 px-6 sm:px-12 lg:px-20">
          <Eyebrow className="text-[var(--launch-accent)]">{t("proofEyebrow")}</Eyebrow>
          <h2 className="bb-reveal bb-impact max-w-5xl text-[clamp(3rem,8vw,9rem)]" style={{ ["--d" as string]: "150ms" }}>{t("proofTitle")}</h2>
          <Link href="/home" className="bb-reveal inline-flex items-center gap-2 rounded-full bg-[var(--launch-accent)] px-6 py-3 text-lg font-semibold text-[var(--launch-on-accent)] hover:brightness-110" style={{ ["--d" as string]: "350ms" }}>
            {t("proofCta")}
            <ArrowRight className="h-5 w-5" aria-hidden />
          </Link>
        </div>
      </Slide>

      {/* 9 · Standing on the shoulders of… */}
      <Slide id="tribute" tone="light">
        <div className="space-y-8">
          <Eyebrow className="text-[var(--launch-accent)]">{t("tributeEyebrow")}</Eyebrow>
          <ul className="grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {TRIBUTE.map((p, i) => (
              <li key={p.name} className="bb-reveal" style={{ ["--d" as string]: `${120 + i * 70}ms` }}>
                <p className="bb-impact text-[clamp(1.5rem,2.5vw,2.75rem)] text-[var(--launch-fg)]">{p.name}</p>
                {(p.note || p.noteKey) && (
                  <p className="mt-1 text-sm sm:text-base lg:text-lg text-[var(--launch-accent)]">{p.noteKey ? t(p.noteKey) : p.note}{p.more && ` · ${t("tributeMore")}`}</p>
                )}
              </li>
            ))}
          </ul>
          <p className="bb-reveal max-w-4xl text-xl sm:text-3xl font-semibold" style={{ ["--d" as string]: `${200 + TRIBUTE.length * 70}ms` }}>{t("tributeClosing")}</p>
        </div>
      </Slide>

      {/* 10 · Why Breakin' Bread — the /why-bb page, as slides (Gordon: "this page
          should be also simulated and better layout on the /launch page") */}
      <Slide id="why">
        <div className="space-y-10">
          <Eyebrow className="text-[var(--launch-accent-2)]">{tw("badge")}</Eyebrow>
          <h2 className="bb-reveal bb-impact text-[clamp(2.75rem,7vw,7.5rem)]" style={{ ["--d" as string]: "120ms" }}>{tw("heroTitle")}</h2>
          <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { Icon: Zap, title: tw("fasterTitle"), body: tw("fasterBody") },
              { Icon: Heart, title: tw("directTitle"), body: tw("directBody") },
              { Icon: Eye, title: tw("verifiableTitle"), body: tw("verifiableBody") },
              { Icon: KeyRound, title: tw("decisionTitle"), body: tw("decisionBody") },
            ].map(({ Icon, title, body }, i) => (
              <div key={title} className="bb-reveal border-t-2 border-[var(--launch-bg)] pt-5" style={{ ["--d" as string]: `${300 + i * 200}ms` }}>
                <Icon className="h-8 w-8 text-[var(--launch-accent-2)]" aria-hidden />
                <p className="bb-impact mt-4 text-[clamp(1.75rem,2.4vw,2.5rem)]">{title}</p>
                <p className="mt-2 text-base sm:text-lg leading-relaxed opacity-75">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* 11 · For makers, for lovers, and what BB is not */}

      {/* 12 · The Founding Circle — "we're not looking for investors but angels for
          the cause" (Gordon 2026-11-02). Names only with each person's OK
          (src/data/founding-circle.ts); amounts are never shown. */}
      <Slide id="founding-circle" tone="accent">
        <div className="space-y-10">
          <div className="max-w-5xl space-y-6">
            <Eyebrow>{tf("eyebrow")}</Eyebrow>
            <h2 className="bb-reveal bb-impact bb-impact-tight text-[clamp(3rem,8vw,8rem)]" style={{ ["--d" as string]: "120ms", ["--bb-tight" as string]: "0.92" }}>{tf("title")}</h2>
          </div>
          {/* The angels — three groups on a shared first place, all equally visible. */}
          <div className="bb-reveal space-y-3" style={{ ["--d" as string]: "250ms" }}>
            <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.2em] opacity-75">{tf("groupsLabel")}</p>
            <ul className="grid gap-3 sm:grid-cols-3">
              {FOUNDING_CIRCLE_GROUPS.map(k => (
                <li key={k} className="bb-impact rounded-2xl border-2 border-current px-5 py-4 text-[clamp(1.5rem,2.8vw,3rem)]">{tf(k)}</li>
              ))}
            </ul>
          </div>
          <p className="bb-reveal max-w-5xl text-xl sm:text-3xl font-semibold leading-snug" style={{ ["--d" as string]: "400ms" }}>{tf("cause")}</p>
        </div>
      </Slide>

      {/* 13 · The Founding Circle, part two — what it means and how to join
          (one dense slide shrank to half size, so it is two). */}
      <Slide id="founding-circle-join">
        <div className="space-y-10">
          <div className="max-w-5xl space-y-6">
            <Eyebrow className="text-[var(--launch-accent)]">{tf("eyebrow")}</Eyebrow>
            <p className="bb-reveal text-xl sm:text-3xl font-semibold leading-snug" style={{ ["--d" as string]: "150ms" }}>{keepBrandTogether(tf("body"))} {tf("noShare")}</p>
            <p className="bb-reveal bb-impact text-[clamp(1.75rem,3.2vw,3rem)] text-[var(--launch-accent)]" style={{ ["--d" as string]: "300ms" }}>{tf("hearYou")}</p>
          </div>
          <div className="grid gap-10 lg:grid-cols-2">
            <div className="bb-reveal" style={{ ["--d" as string]: "600ms" }}>
              <p className="bb-impact text-[clamp(1.5rem,2.4vw,2.25rem)]">{tf("perksTitle")}</p>
              <ul className="mt-3">
                {FOUNDING_CIRCLE_PERKS.map(k => (
                  <li key={k} className="flex gap-3 border-t border-[color-mix(in_srgb,currentColor_20%,transparent)] py-3 text-lg sm:text-xl leading-snug">
                    <ArrowRight className="mt-1 h-5 w-5 shrink-0" aria-hidden />
                    <span>{tf(k)}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Just the way in (Gordon 2026-11-02: the "In the circle" list, its
                empty note and the amounts line are gone from the slide). */}
            <div className="bb-reveal flex items-end" style={{ ["--d" as string]: "750ms" }}>
              <Link href={FOUNDING_CIRCLE_JOIN} className="inline-flex items-center gap-2 rounded-full border-2 border-current px-6 py-3 text-lg sm:text-xl font-semibold hover:opacity-80">
                {tf("cta")} <ArrowRight className="h-5 w-5" aria-hidden />
              </Link>
            </div>
          </div>
          <p className="bb-reveal bb-impact text-[clamp(1.75rem,3.5vw,3.25rem)]" style={{ ["--d" as string]: "900ms" }}>{tf("slogan")}</p>
        </div>
      </Slide>

      {/* 14 · Buy vs stream — the /why-bb calculator as a slide, every platform at once
          (Gordon: "should not be a link but should be a section on the launch page") */}
      <Slide id="buy" tone="light">
        <BuyVsStream />
      </Slide>

      {/* 15 · Join the relaunch */}
      <Slide id="join">
        <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-6">
            <Eyebrow className="text-[var(--launch-accent)]">{t("joinEyebrow")}</Eyebrow>
            {/* Two rows, set close (Gordon 2026-11-02: "Kom och / mingla … a bit wide
                between the rows"): plain letters get tight rows in any language;
                titles with accents keep the room their marks need. */}
            <h2 className="bb-reveal bb-impact bb-impact-tight whitespace-pre-line text-[clamp(3.5rem,9vw,9.5rem)]" style={{ ["--d" as string]: "120ms", ["--bb-tight" as string]: "0.88", ...(/^[\x20-\x7e\n]+$/.test(t("joinTitle")) ? { lineHeight: 0.92 } : null) }}>{t("joinTitle")}</h2>
            <p className="bb-reveal bb-impact whitespace-pre-line text-[clamp(1.75rem,3.5vw,3.25rem)] text-[var(--launch-accent)]" style={{ ["--d" as string]: "300ms" }}>{t("joinBody")}</p>
            <p className="bb-reveal text-lg sm:text-2xl font-semibold" style={{ ["--d" as string]: "450ms" }}>breakinbread.com · {t("venue")}</p>
          </div>
          {/* The caption sets the column width and the code fills it, so both
              are exactly as wide (Gordon 2026-11-01: "same width on qr code as
              the text below"); the code adds no width of its own (contain), a
              short translation still keeps it scannable (min-w). The BB
              signature sits under them, never on the caption. */}
          {/* The caption spans exactly the code's width (Gordon 2026-11-02): the
              code has a set size, the caption is justified across it. */}
          <div className="bb-reveal mr-auto flex w-[min(20rem,80vw)] max-w-full flex-col gap-4 sm:w-[22rem]" style={{ ["--d" as string]: "500ms" }}>
            {/* Always white behind the code — a scanner needs a light quiet zone, whatever the theme. */}
            <div className="relative aspect-square w-full rounded-3xl bg-white p-5 shadow-2xl [contain:inline-size]">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={qr} alt={t("scanHint")} width={480} height={480} className="h-full w-full" />
              {/* The BB logo in the middle (Gordon 2026-11-02) — under a fifth of the code, which ecc=H rides out. */}
              <span aria-hidden className="absolute left-1/2 top-1/2 flex aspect-square w-[22%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl bg-white">
                <span className="block h-[78%] w-[78%] bg-black" style={{ WebkitMaskImage: "url(/brand/bb-logo.svg)", maskImage: "url(/brand/bb-logo.svg)", WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskPosition: "center", maskPosition: "center" }} />
              </span>
            </div>
            <BBSignature className="mt-6 self-end h-14 sm:h-16 bg-[var(--launch-fg)]" />
          </div>
        </div>
      </Slide>
    </div>
  );
}
